import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';

import { IDateRange } from '../../../providers/settings/dates/dateRangeProvider';
import { IAsymmetryReportSettings } from '../../../providers/settings/reports/asymmetryReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';

/**
 * @description updates the asymmetry template
 * @param template
 * @param isSelected
 */
export const updateTemplate = async (
  template: ISettingsTemplate<IAsymmetryReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();
  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/asymmetryReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/asymmetryReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/asymmetryReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description creates a new report settings template
 * @param template
 */
export const createTemplate = async (
  template: Partial<ISettingsTemplate<IAsymmetryReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();
  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/asymmetryReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};

/**
 * @description saves the template as the selected template and
 * @param template
 */
export const saveSettings = async (
  template: ISettingsTemplate<IAsymmetryReportSettings>,
  timeframe: IDateRange,
  baselineTimeframe: IDateRange
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/asymmetryReport`
  );

  await setDoc(
    docRef,
    {
      selected: template ?? null,
      timeframe,
      baselineTimeframe,
    },
    { merge: true }
  );
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<IAsymmetryReportSettings>,
  replacement: ISettingsTemplate<IAsymmetryReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);

  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/asymmetryReport/templates/${template.id}`
  );

  const update: ISettingsTemplate<IAsymmetryReportSettings> = {
    ...template,
    active: false,
  };

  batch.update(
    updateRef,
    update as unknown as Record<string, Partial<unknown>>
  );

  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/asymmetryReport`
  );

  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });

  return batch.commit();
};
